// src/hooks/useJobSchemas.js
// Hook for fetching and managing dynamic job type schemas from the backend
//
// Architecture:
// - Backend schema provides: job types, descriptions, parameters (types, required, defaults)
// - WorkflowDefinitions provides: UI grouping (workflows, categories, icons)
// - This hook combines both into a unified interface

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGraphQL } from '../graphql';
import { getCategoryForJobType, getCategories } from '../components/jobs/workflows/WorkflowDefinitions';

/**
 * Mapping from backend job types (snake_case) to mutation keys
 *
 * This mapping is necessary because GraphQL mutation names don't follow
 * a consistent pattern that can be derived from job type names.
 * This is the ONLY static job-related mapping we need to maintain.
 */
const JOB_TYPE_TO_MUTATION = {
    'check_new': 'adminCheckNewTenders',
    'crawl': 'adminCrawlTender',
    'crawl_all': 'adminCrawlAllTenders',
    'sync': 'adminSyncTenders',
    'deep_crawl': 'adminDeepCrawlTender',
    'deep_crawl_all': 'adminDeepCrawlAll',
    'deep_crawl_by_domain': 'adminDeepCrawlByDomain',
    'assess_tender': 'adminAssessTender',
    'assess_all_tenders': 'adminAssessAllTenders',
    'generate_narrative_assessment': 'generateTenderNarrativeAssessment',
    'prequalify_tenders': 'prequalifyTenders',
    'refresh_documents': 'adminRefreshDocuments',
    'geocode': 'adminGeocodeTender',
    'geocode_all': 'adminGeocodeAllTenders',
    'extract_document_text': 'adminExtractDocumentText',
    'extract_tender_documents_text': 'adminExtractTenderDocumentsText',
    'extract_all_documents_text': 'adminExtractAllDocumentsText',
    'convert_documents': 'adminConvertTenderDocuments',
    'convert_all_documents': 'adminConvertAllDocuments',
    'normalize_documents': 'adminNormalizeDocuments',
    'index_tender': 'adminIndexTender',
    'index_all_tenders': 'adminIndexAllTenders',
    'search_corpus': 'adminSearchCorpus',
    'rescore_corpus_v3': 'adminRescoreCorpusV3',
    'recreate_index': 'adminRecreateIndex',
    'reverse_recreate_index': 'adminReverseRecreateIndex',
    'reverse_index_all_fingerprints': 'adminReverseIndexAllFingerprints',
    'send_saved_search_reports': 'adminSendSavedSearchReports',
    'generate_ted_query': 'adminGenerateTedQuery',
    'send_email': 'adminSendEmail',
    'run_workflow': 'adminRunWorkflow',
    'run_company_workflows': 'adminRunCompanyWorkflows',
};

/**
 * Bulk/orchestration job types that are intentionally routed through a wrapping
 * workflow via adminRunWorkflow (so the run is visible in the Workflows UI),
 * even though the underlying job could also be enqueued directly. The modal
 * sends the entered JSON as the workflow input. Job types NOT listed here and
 * lacking a dedicated mutation fall through to the generic enqueue trigger.
 */
const JOB_TYPE_TO_WORKFLOW_TEMPLATE = {
    'generate_narratives_bulk': 'bulk-narrative-generation',
};

/**
 * Generic job-trigger mutation — the resilient fallback. Any job type without a
 * dedicated mutation (above) or a workflow wrapper routes through this: the
 * frontend sends the snake_case job type + raw JSON params, and the backend
 * validates the type against its job-schema registry (the same set surfaced by
 * jobTypeSchemas) before enqueuing. This is what keeps the Create Job modal in
 * sync with the engine: a newly registered job type becomes triggerable the
 * moment it appears in jobTypeSchemas, with no mapping entry to hand-maintain.
 */
const GENERIC_ENQUEUE_MUTATION = 'adminEnqueueJob';

/**
 * Mutations that can carry a cron schedule. adminRunWorkflow /
 * adminRunCompanyWorkflows / adminEnqueueJob take it as a top-level argument;
 * adminCheckNewTenders takes it inside CheckNewTendersInput. Every other
 * dedicated job mutation has no cron argument at all.
 */
const CRON_CAPABLE_MUTATIONS = new Set([
    GENERIC_ENQUEUE_MUTATION,
    'adminRunWorkflow',
    'adminRunCompanyWorkflows',
    'adminCheckNewTenders',
]);

/**
 * Resolve the mutation to submit with, given whether a cron schedule is set.
 * Scheduled runs of job types whose dedicated mutation can't carry a cron are
 * re-routed through the generic enqueue — previously the modal's "Scheduled"
 * toggle was silently dropped for those types and a one-time job was created.
 */
export const resolveMutationKey = (jobType, cronSchedule) => {
    if (!jobType) return null;
    if (jobType.workflowTemplate) return 'adminRunWorkflow';
    if (cronSchedule && !CRON_CAPABLE_MUTATIONS.has(jobType.mutationKey)) {
        return GENERIC_ENQUEUE_MUTATION;
    }
    return jobType.mutationKey;
};

/**
 * Jobs that use an `input` wrapper for their parameters
 * This is determined by the GraphQL schema design.
 */
const JOBS_WITH_INPUT_WRAPPER = [
    'check_new',
    'prequalify_tenders',
    'deep_crawl_by_domain',
    'send_email',
];

/**
 * Convert snake_case to UPPER_CASE
 */
const toUpperCase = (str) => str?.toUpperCase() || '';

/**
 * Convert snake_case to Title Case label
 */
const toTitleCase = (str) => {
    if (!str) return '';
    return str
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Convert Go type to JavaScript/JSON type hint
 */
const goTypeToJSType = (goType) => {
    if (!goType) return 'string';
    const type = goType.replace(/^\*/, '');

    switch (type) {
        case 'string':
            return 'string';
        case 'int':
        case 'int32':
        case 'int64':
        case 'float32':
        case 'float64':
            return 'number';
        case 'bool':
            return 'boolean';
        default:
            if (type.startsWith('[]')) {
                return 'array';
            }
            return 'object';
    }
};

/**
 * Parse a default value from string to appropriate type
 */
const parseDefault = (defaultStr, goType) => {
    if (defaultStr === null || defaultStr === undefined || defaultStr === '') {
        return null;
    }

    const jsType = goTypeToJSType(goType);

    switch (jsType) {
        case 'number': {
            const num = parseFloat(defaultStr);
            return isNaN(num) ? null : num;
        }
        case 'boolean':
            return defaultStr === 'true';
        case 'array':
            try {
                return JSON.parse(defaultStr);
            } catch {
                return [];
            }
        default:
            return defaultStr;
    }
};

/**
 * Hook for fetching and managing job type schemas
 *
 * @returns {Object} Job schemas state and utilities
 */
export const useJobSchemas = () => {
    const { executeQuery, queries } = useGraphQL();
    const [schemas, setSchemas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);

    /**
     * Fetch all job type schemas from the backend
     * Note: This is intentionally not in useCallback to avoid infinite loops
     * caused by executeQuery/queries changing references on each render
     */
    const fetchSchemas = async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await executeQuery(queries.getJobTypeSchemas());

            if (data?.jobTypeSchemas) {
                setSchemas(data.jobTypeSchemas);
                setLastFetched(new Date());
            }
        } catch (err) {
            console.error('Failed to fetch job schemas:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch schemas on mount only (empty dependency array)
     */
    useEffect(() => {
        fetchSchemas();
    }, []);

    /**
     * Get enhanced job type data with derived metadata
     * Sorted alphabetically by label for better UX
     */
    const jobTypes = useMemo(() => {
        const mapped = schemas.map(schema => {
            const jobType = schema.jobType; // snake_case from backend
            const upperType = toUpperCase(jobType);
            const workflowTemplate = JOB_TYPE_TO_WORKFLOW_TEMPLATE[jobType] || null;

            // Derive requirements from schema params
            // Note: We show the field if the param EXISTS (hasTenderId), and mark it
            // as mandatory in the form if it's REQUIRED (requiresTenderId)
            const companyIdParam = schema.params?.find(p => p.jsonName === 'companyId');
            const tenderIdParam = schema.params?.find(p => p.jsonName === 'tenderId');
            const documentIdParam = schema.params?.find(p => p.jsonName === 'documentId');

            return {
                // Original schema data
                ...schema,

                // Normalized identifiers
                value: upperType,           // UPPER_CASE for form values
                key: jobType,               // snake_case original

                // UI metadata - label from schema description or derived from name
                label: toTitleCase(jobType),
                // Category comes from WorkflowDefinitions (single source of truth)
                category: getCategoryForJobType(upperType),

                // Mutation routing, in precedence order:
                //   1. workflow-wrapped bulk job → adminRunWorkflow(templateName)
                //   2. dedicated typed mutation   → e.g. adminCrawlTender
                //   3. anything else              → generic adminEnqueueJob
                // The generic fallback means a newly added engine job type is
                // triggerable immediately, with no mapping entry to maintain —
                // so the modal no longer dead-ends on "no mutation mapped".
                mutationKey: workflowTemplate
                    ? 'adminRunWorkflow'
                    : (JOB_TYPE_TO_MUTATION[jobType] || GENERIC_ENQUEUE_MUTATION),
                workflowTemplate,
                usesInput: JOBS_WITH_INPUT_WRAPPER.includes(jobType),
                // True when the job type has no dedicated mutation and no
                // workflow wrapper, so it routes through the generic trigger.
                // Used by the modal to show an informational (not blocking) hint.
                usesGenericEnqueue: !workflowTemplate && !JOB_TYPE_TO_MUTATION[jobType],

                // Field visibility (show field if param exists in schema)
                hasTenderId: !!tenderIdParam,
                hasDocumentId: !!documentIdParam,
                hasCompanyId: !!companyIdParam,

                // Field requirements (mark as mandatory if schema says required)
                requiresTenderId: tenderIdParam?.required || false,
                requiresDocumentId: documentIdParam?.required || false,
                requiresCompany: companyIdParam?.required || false,
                optionalCompany: !!companyIdParam && !companyIdParam.required,

                // Parameter metadata for easy lookup
                paramsByName: Object.fromEntries(
                    (schema.params || []).map(p => [p.jsonName, p])
                ),
            };
        });

        // Sort alphabetically by label
        return mapped.sort((a, b) => a.label.localeCompare(b.label));
    }, [schemas]);

    /**
     * Get job types organized by category (each category sorted alphabetically)
     */
    const jobTypesByCategory = useMemo(() => {
        const categoriesMap = {};
        jobTypes.forEach(jt => {
            if (!categoriesMap[jt.category]) {
                categoriesMap[jt.category] = [];
            }
            categoriesMap[jt.category].push(jt);
        });
        // Sort each category's job types alphabetically
        Object.keys(categoriesMap).forEach(cat => {
            categoriesMap[cat].sort((a, b) => a.label.localeCompare(b.label));
        });
        return categoriesMap;
    }, [jobTypes]);

    /**
     * Get a job type schema by its identifier (supports both snake_case and UPPER_CASE)
     */
    const getJobType = useCallback((identifier) => {
        const normalized = identifier?.toLowerCase().replace(/-/g, '_');
        return jobTypes.find(jt => jt.key === normalized || jt.value === identifier?.toUpperCase());
    }, [jobTypes]);

    /**
     * Generate a parameters placeholder JSON from a job type schema
     */
    const getParametersPlaceholder = useCallback((identifier) => {
        const jobType = getJobType(identifier);
        if (!jobType?.params?.length) {
            return '{}';
        }

        const placeholder = {};
        jobType.params.forEach(param => {
            // Skip tenderId, documentId, companyId as they have dedicated form fields
            if (['tenderId', 'documentId', 'companyId'].includes(param.jsonName)) {
                return;
            }

            // Use default value or generate appropriate placeholder
            let value = parseDefault(param.default, param.type);

            if (value === null) {
                const jsType = goTypeToJSType(param.type);
                switch (jsType) {
                    case 'boolean':
                        value = param.required ? true : null;
                        break;
                    case 'number':
                        value = null;
                        break;
                    case 'array':
                        value = [];
                        break;
                    case 'string':
                    default:
                        value = null;
                        break;
                }
            }

            if (value !== null || param.required) {
                placeholder[param.jsonName] = value;
            }
        });

        return JSON.stringify(placeholder, null, 2);
    }, [getJobType]);

    /**
     * Generate a complete parameter template with ALL params (for pre-fill functionality)
     * Unlike getParametersPlaceholder, this includes all optional params with sensible defaults
     */
    const getFullParameterTemplate = useCallback((identifier) => {
        const jobType = getJobType(identifier);
        if (!jobType?.params?.length) {
            return '{}';
        }

        const template = {};
        jobType.params.forEach(param => {
            // Skip tenderId, documentId, companyId as they have dedicated form fields
            if (['tenderId', 'documentId', 'companyId'].includes(param.jsonName)) {
                return;
            }

            // Use default value or generate appropriate placeholder
            let value = parseDefault(param.default, param.type);

            if (value === null) {
                const jsType = goTypeToJSType(param.type);
                switch (jsType) {
                    case 'boolean':
                        value = false;
                        break;
                    case 'number':
                        value = 0;
                        break;
                    case 'array':
                        value = [];
                        break;
                    case 'string':
                    default:
                        value = '';
                        break;
                }
            }

            // Always include the param in the template
            template[param.jsonName] = value;
        });

        return JSON.stringify(template, null, 2);
    }, [getJobType]);

    // ============================================================================
    // Local Storage History Management
    // ============================================================================
    const HISTORY_STORAGE_KEY = 'elvis_job_params_history';
    const MAX_HISTORY_PER_JOB = 5;

    /**
     * Get all parameter history from localStorage
     */
    const getParamsHistory = useCallback(() => {
        try {
            const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    }, []);

    /**
     * Get parameter history for a specific job type
     * Returns array of { params: string, timestamp: number, label?: string }
     */
    const getParamsHistoryForJob = useCallback((identifier) => {
        const jobType = getJobType(identifier);
        if (!jobType) return [];

        const history = getParamsHistory();
        return history[jobType.key] || [];
    }, [getJobType, getParamsHistory]);

    /**
     * Save parameters to history for a job type
     */
    const saveParamsToHistory = useCallback((identifier, params) => {
        const jobType = getJobType(identifier);
        if (!jobType) return;

        // Don't save empty params
        if (!params || params === '{}' || params.trim() === '') return;

        try {
            const history = getParamsHistory();
            const jobHistory = history[jobType.key] || [];

            // Check if this exact params already exists
            const existingIndex = jobHistory.findIndex(h => h.params === params);
            if (existingIndex !== -1) {
                // Move to front and update timestamp
                const existing = jobHistory.splice(existingIndex, 1)[0];
                existing.timestamp = Date.now();
                jobHistory.unshift(existing);
            } else {
                // Add new entry
                jobHistory.unshift({
                    params,
                    timestamp: Date.now(),
                });
            }

            // Keep only last N entries
            history[jobType.key] = jobHistory.slice(0, MAX_HISTORY_PER_JOB);

            localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
        } catch (err) {
            console.error('Failed to save params to history:', err);
        }
    }, [getJobType, getParamsHistory]);

    /**
     * Clear history for a specific job type
     */
    const clearParamsHistoryForJob = useCallback((identifier) => {
        const jobType = getJobType(identifier);
        if (!jobType) return;

        try {
            const history = getParamsHistory();
            delete history[jobType.key];
            localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
        } catch (err) {
            console.error('Failed to clear params history:', err);
        }
    }, [getJobType, getParamsHistory]);

    /**
     * Build mutation variables from form data based on job type schema
     */
    const buildVariables = useCallback((identifier, formData, selectedCompanyId) => {
        const jobType = getJobType(identifier);
        if (!jobType) {
            console.warn(`Unknown job type: ${identifier}`);
            return {};
        }

        // Parse the parameters JSON
        let params = {};
        if (formData.parameters?.trim() && formData.parameters !== '{}') {
            try {
                params = JSON.parse(formData.parameters);
            } catch {
                // Ignore parse errors, use empty params
            }
        }

        // Build variables from schema
        const variables = {};

        // Add standard fields if present in schema
        if (jobType.paramsByName.tenderId) {
            variables.tenderId = formData.tenderId || params.tenderId || null;
        }
        if (jobType.paramsByName.documentId) {
            variables.documentId = formData.documentId || params.documentId || null;
        }
        if (jobType.paramsByName.companyId) {
            variables.companyId = selectedCompanyId || params.companyId || null;
        }

        // Add other parameters from schema
        jobType.params?.forEach(param => {
            if (['tenderId', 'documentId', 'companyId'].includes(param.jsonName)) {
                return;
            }

            const value = params[param.jsonName];
            if (value !== undefined && value !== null && value !== '') {
                const jsType = goTypeToJSType(param.type);
                switch (jsType) {
                    case 'number':
                        variables[param.jsonName] = parseInt(value, 10) || parseFloat(value) || null;
                        break;
                    case 'boolean':
                        variables[param.jsonName] = Boolean(value);
                        break;
                    case 'array':
                        variables[param.jsonName] = Array.isArray(value) ? value : null;
                        break;
                    default:
                        variables[param.jsonName] = value;
                }
            } else if (param.default !== null && param.default !== undefined) {
                variables[param.jsonName] = parseDefault(param.default, param.type);
            }
        });

        // Cron expression, when the user toggled "Scheduled" in the modal.
        // Only three mutations accept it natively (adminRunWorkflow,
        // adminRunCompanyWorkflows, adminEnqueueJob) plus check_new via its
        // input type — resolveMutationKey() below re-routes every other
        // scheduled job through the generic enqueue so the cron is never
        // silently dropped.
        const cron = formData.cronSchedule?.trim() || '';

        // Workflow-routed bulk job: the entered JSON *is* the workflow input,
        // sent through adminRunWorkflow($templateName, $input). Use the raw
        // parsed params (not schema-built variables) so omitted optional fields
        // fall through to the workflow's own server-side defaults rather than
        // being sent as null.
        if (jobType.workflowTemplate) {
            const wf = { templateName: jobType.workflowTemplate, input: params };
            if (cron) wf.cronSchedule = cron;
            return wf;
        }

        // Generic enqueue: no dedicated mutation / no workflow wrapper, or a
        // scheduled run of a dedicated-mutation type (dedicated mutations don't
        // accept a cron). Send the snake_case job type + raw params through
        // adminEnqueueJob. We deliberately forward the user's raw params (not
        // the schema-built `variables`, which fill/coerce defaults): backend
        // handlers seed their own struct defaults before json.Unmarshal, so an
        // omitted key keeps the documented default, whereas sending a
        // zero-value (e.g. limit: 0) would override it. tenderId is sent
        // top-level (it populates job.TenderID); companyId/documentId are
        // merged into params where handlers read them.
        if (resolveMutationKey(jobType, cron) === GENERIC_ENQUEUE_MUTATION) {
            const parameters = { ...params };
            if (jobType.paramsByName.companyId) {
                const companyId = selectedCompanyId || params.companyId;
                if (companyId) parameters.companyId = companyId;
            }
            if (jobType.paramsByName.documentId) {
                const documentId = formData.documentId || params.documentId;
                if (documentId) parameters.documentId = documentId;
            }
            const generic = { type: jobType.key, parameters };
            if (jobType.paramsByName.tenderId) {
                const tenderId = formData.tenderId || params.tenderId;
                if (tenderId) generic.tenderId = tenderId;
            }
            if (cron) generic.cronSchedule = cron;
            return generic;
        }

        // Wrap in input if needed
        if (jobType.usesInput) {
            // CheckNewTendersInput is the one input type with a cronSchedule
            // field (the other cron-capable mutations take it top-level).
            if (cron && jobType.key === 'check_new') {
                variables.cronSchedule = cron;
            }
            return { input: variables };
        }

        // Dedicated run_workflow / run_company_workflows mutations take the
        // cron as a top-level argument.
        if (cron && (jobType.mutationKey === 'adminRunWorkflow' || jobType.mutationKey === 'adminRunCompanyWorkflows')) {
            variables.cronSchedule = cron;
        }

        return variables;
    }, [getJobType]);

    /**
     * Categories derived from WorkflowDefinitions (single source of truth)
     */
    const categories = useMemo(() => getCategories(), []);

    return {
        // State
        schemas,
        jobTypes,
        jobTypesByCategory,
        categories,
        loading,
        error,
        lastFetched,

        // Actions
        fetchSchemas,
        getJobType,
        getParametersPlaceholder,
        getFullParameterTemplate,
        buildVariables,

        // History management
        getParamsHistoryForJob,
        saveParamsToHistory,
        clearParamsHistoryForJob,

        // Utilities
        toUpperCase,
        toTitleCase,
        goTypeToJSType,
    };
};

export default useJobSchemas;
